/**
 * Custom cart drawer — replaces the third-party Slide Cart app.
 *
 * Uses Shopify's Section Rendering API: every cart mutation POSTs to the
 * standard /cart/*.js endpoints with a `sections` param, and the returned
 * HTML for the `cart-drawer` section is swapped straight in. All discount
 * de-duplication and sample-cap logic stays server-side Liquid (see
 * sections/cart-drawer.liquid) — this file only handles wiring, not
 * business logic.
 *
 * Also shims window.SLIDECART_OPEN / SLIDECART_CLOSE / SLIDECART_UPDATE and
 * fires the `slidecart:updated` window event, so assets/gift-with-purchase.js
 * (a separate, unrelated feature) keeps working unmodified.
 */
(function () {
  var SECTION_ID = 'cart-drawer';

  function drawerRoot() {
    return document.getElementById('CustomCartDrawer');
  }

  function sectionWrapper() {
    return document.getElementById('shopify-section-' + SECTION_ID);
  }

  function open() {
    var root = drawerRoot();
    if (!root) return;
    root.classList.add('is-open');
    document.documentElement.classList.add('ccd-open');
    // The legacy jQuery cart toggle (assets/shop.js.liquid, still bound to
    // the same .js-cartToggle-js header icon) also flips this class; force
    // it here so scroll-lock (see snippets/theme-style.liquid) stays correct
    // no matter which handler fired last or whether the drawer was opened
    // programmatically (e.g. after an add-to-cart) without a real icon click.
    document.body.classList.add('cart--is-visible');
  }

  function close() {
    var root = drawerRoot();
    if (!root) return;
    root.classList.remove('is-open');
    document.documentElement.classList.remove('ccd-open');
    document.body.classList.remove('cart--is-visible');
    document.body.style.width = '';
  }

  function syncBadge(cart) {
    var badge = document.getElementById('CartToggleItemCount');
    if (badge) {
      badge.textContent = cart.item_count;
      badge.setAttribute('data-count', cart.item_count);
    }
  }

  function render(sections) {
    var html = sections && sections[SECTION_ID];
    if (!html) return;

    // Swapping the section's innerHTML replaces #CustomCartDrawer itself with
    // a fresh server-rendered copy, which never has `is-open` on it (that
    // class only ever gets added client-side). Without restoring it, the
    // drawer would visually close on every cart mutation (quantity change,
    // discount apply, sample add, ...) while it's open.
    var wasOpen = !!(drawerRoot() && drawerRoot().classList.contains('is-open'));

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newSection = doc.getElementById('shopify-section-' + SECTION_ID);
    var current = sectionWrapper();
    if (newSection && current) {
      current.innerHTML = newSection.innerHTML;
    }

    if (wasOpen) {
      var root = drawerRoot();
      if (root) root.classList.add('is-open');
    }

    trimSampleOverflow();
    processAutoGift();
  }

  // sections/cart-drawer.liquid computes, per sample line, how much quantity
  // is still allowed under settings.free_samples_cap and exposes it as
  // data-samples-overflow="key:allowedQty,...". Trim one line at a time —
  // each /cart/change.js call re-renders the section (and re-evaluates this
  // attribute against the now-current cart), so this naturally cascades
  // until nothing is left over the cap, without any client-side guessing
  // about which line items are actually samples.
  function trimSampleOverflow() {
    var root = drawerRoot();
    var overflow = root && root.getAttribute('data-samples-overflow');
    if (!overflow) return;

    var firstAction = overflow.split(',').filter(Boolean)[0];
    if (!firstAction) return;

    var parts = firstAction.split(':');
    var key = parts[0];
    var qty = parseInt(parts[1], 10);
    if (!key || Number.isNaN(qty)) return;

    changeLine(key, qty);
  }

  // sections/cart-drawer.liquid checks whether settings.auto_gift_trigger_product
  // is in the cart and whether the tagged gift line (properties._auto_gift)
  // matches that, exposing data-auto-gift-action="add:<variantId>" or
  // "remove:<key>". Acting on it here re-renders the section, which
  // re-evaluates the same check against the now-current cart — so it
  // self-corrects (add when the trigger appears, remove again once it's
  // gone) without any client-side product/collection knowledge.
  function processAutoGift() {
    var root = drawerRoot();
    var action = root && root.getAttribute('data-auto-gift-action');
    if (!action) return;

    if (action.indexOf('add:') === 0) {
      addToCart(action.slice(4), 1, { _auto_gift: 'true' });
    } else if (action.indexOf('remove:') === 0) {
      changeLine(action.slice(7), 0);
    }
  }

  function applyCartResponse(data) {
    render(data.sections);

    // /cart/add.js only returns the added line item(s), not the full cart,
    // so it has no item_count. /cart/change.js and /cart/update.js do return
    // the full cart. When it's missing, fetch it so the badge still updates
    // immediately instead of waiting for the next cart mutation.
    if (typeof data.item_count === 'number') {
      syncBadge(data);
    } else {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (response) { return response.json(); })
        .then(syncBadge);
    }

    window.dispatchEvent(new CustomEvent('slidecart:updated'));
    window.dispatchEvent(new CustomEvent('cart:changed'));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        return { ok: response.ok, data: data };
      });
    });
  }

  function addToCart(id, quantity, properties) {
    var item = { id: id, quantity: quantity || 1 };
    if (properties) item.properties = properties;
    return postJSON('/cart/add.js', {
      items: [item],
      sections: [SECTION_ID],
    }).then(function (result) {
      if (result.ok) applyCartResponse(result.data);
      return result;
    });
  }

  function changeLine(key, quantity) {
    return postJSON('/cart/change.js', {
      id: key,
      quantity: quantity,
      sections: [SECTION_ID],
    }).then(function (result) {
      if (result.ok) applyCartResponse(result.data);
      return result;
    });
  }

  function refresh() {
    return postJSON('/cart/update.js', { sections: [SECTION_ID] }).then(function (result) {
      if (result.ok) applyCartResponse(result.data);
      return result;
    });
  }

  function applyDiscounts(codes) {
    return postJSON('/cart/update.js', {
      discount: codes.join(','),
      sections: [SECTION_ID],
    }).then(function (result) {
      applyCartResponse(result.data);
      return result;
    });
  }

  function existingDiscountCodes() {
    var pills = document.querySelectorAll('.ccd__discount-pill[data-code]');
    return Array.prototype.map.call(pills, function (el) {
      return el.getAttribute('data-code');
    });
  }

  // Sample lines carry data-max-qty (see sections/cart-drawer.liquid): the most
  // this line may hold before the cart's total sample quantity passes
  // settings.free_samples_cap. Typing a bigger number would otherwise be sent
  // to the cart as-is and only get corrected afterwards by trimSampleOverflow;
  // clamp it here so the field snaps straight back to the cap.
  function clampToMax(input, qty) {
    var max = parseInt(input.getAttribute('data-max-qty'), 10);
    if (Number.isNaN(max) || qty <= max) return qty;
    input.value = max;
    return max;
  }

  // Add-to-cart form intercept (sitewide, e.g. #prod_add_to_cart on product pages,
  // .add-to-bag on the product page's cross-sell bundle) — keeps the shopper on the
  // page and opens the cart drawer instead of a full-page /cart/add navigation.
  document.addEventListener(
    'click',
    function (event) {
      var trigger = event.target.closest('#prod_add_to_cart, .add-to-bag');
      if (!trigger) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      var form = trigger.closest('form[action="/cart/add"]');
      if (!form) return;

      var formData = new FormData(form);
      formData.append('sections', SECTION_ID);

      fetch('/cart/add.js', { method: 'POST', body: formData })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            console.error('Error adding to cart:', result.data);
            return;
          }
          applyCartResponse(result.data);
          open();
        })
        .catch(function (error) {
          console.error('Error adding to cart:', error);
        });
    },
    true
  );

  // Drawer open/close + in-drawer interactions
  document.addEventListener('click', function (event) {
    if (event.target.closest('.js-cartToggle-js')) {
      event.preventDefault();
      open();
      return;
    }

    if (event.target.closest('[data-ccd-close]')) {
      close();
      return;
    }

    var qtyBtn = event.target.closest('.ccd__qty-btn');
    if (qtyBtn) {
      var wrapper = qtyBtn.closest('.ccd__qty');
      var input = wrapper.querySelector('.ccd__qty-input');
      var key = wrapper.getAttribute('data-key');
      var qty = parseInt(input.value || '0', 10);
      if (Number.isNaN(qty)) qty = 0;
      qty = qtyBtn.getAttribute('data-action') === 'increase' ? qty + 1 : qty - 1;
      if (qty < 0) qty = 0;
      qty = clampToMax(input, qty);
      qtyBtn.disabled = true;
      changeLine(key, qty);
      return;
    }

    var removeBtn = event.target.closest('.ccd__item-remove');
    if (removeBtn) {
      changeLine(removeBtn.getAttribute('data-key'), 0);
      return;
    }

    var samplesNavBtn = event.target.closest('.ccd__samples-nav');
    if (samplesNavBtn) {
      var carousel = samplesNavBtn.closest('.ccd__samples-carousel');
      var row = carousel && carousel.querySelector('.ccd__samples-row');
      if (row) {
        var direction = samplesNavBtn.getAttribute('data-ccd-samples-scroll') === 'prev' ? -1 : 1;
        var step = row.clientWidth * 0.9;
        if (row.scrollBy) {
          row.scrollBy({ left: direction * step, behavior: 'smooth' });
        } else {
          row.scrollLeft += direction * step;
        }
      }
      return;
    }

    var sampleAddBtn = event.target.closest('.ccd__sample-add');
    if (sampleAddBtn && !sampleAddBtn.disabled) {
      var originalLabel = sampleAddBtn.textContent;
      sampleAddBtn.disabled = true;
      sampleAddBtn.textContent = '...';
      addToCart(sampleAddBtn.getAttribute('data-variant-id'), 1).then(function (result) {
        if (!result.ok) {
          sampleAddBtn.disabled = false;
          sampleAddBtn.textContent = originalLabel;
        }
      });
      return;
    }

    var removeDiscountBtn = event.target.closest('[data-ccd-remove-discount]');
    if (removeDiscountBtn) {
      var code = removeDiscountBtn.getAttribute('data-ccd-remove-discount');
      var remaining = existingDiscountCodes().filter(function (c) {
        return c !== code;
      });
      applyDiscounts(remaining);
    }
  });

  // Click-and-drag ("slepen") to scroll the sample tile row with a mouse,
  // since overflow-x: auto alone only supports scrollbar-drag/touch, not a
  // plain mouse drag on the content itself.
  var sampleDrag = null;
  var suppressSampleClick = false;

  document.addEventListener('mousedown', function (event) {
    var row = event.target.closest('.ccd__samples-row');
    if (!row) return;
    sampleDrag = { row: row, startX: event.pageX, startScrollLeft: row.scrollLeft, moved: false };
    row.classList.add('is-dragging');
  });

  document.addEventListener('mousemove', function (event) {
    if (!sampleDrag) return;
    var delta = event.pageX - sampleDrag.startX;
    if (Math.abs(delta) > 3) sampleDrag.moved = true;
    sampleDrag.row.scrollLeft = sampleDrag.startScrollLeft - delta;
    event.preventDefault();
  });

  function endSampleDrag() {
    if (!sampleDrag) return;
    sampleDrag.row.classList.remove('is-dragging');
    if (sampleDrag.moved) suppressSampleClick = true;
    sampleDrag = null;
  }

  document.addEventListener('mouseup', endSampleDrag);
  document.addEventListener('mouseleave', endSampleDrag);

  // A drag ending on a link/button inside the row (e.g. a product image)
  // would otherwise also fire a click right after and navigate away/add a
  // sample; swallow that one click.
  document.addEventListener(
    'click',
    function (event) {
      if (suppressSampleClick) {
        suppressSampleClick = false;
        if (event.target.closest('.ccd__samples-row')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    },
    true
  );

  // The sample tile row only scrolls horizontally (overflow-x: auto); a plain
  // mouse wheel scrolls vertically and does nothing there by default, so
  // translate vertical wheel delta into horizontal scrolling ourselves.
  document.addEventListener(
    'wheel',
    function (event) {
      var row = event.target.closest('.ccd__samples-row');
      if (!row) return;
      if (row.scrollWidth <= row.clientWidth) return;

      var delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      row.scrollLeft += delta;
      event.preventDefault();
    },
    { passive: false }
  );

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-ccd-discount-form]');
    if (!form) return;
    event.preventDefault();

    var input = form.querySelector('input[name="discount"]');
    var value = (input.value || '').trim();
    if (!value) return;

    var codes = existingDiscountCodes();
    if (codes.indexOf(value) !== -1) return;

    applyDiscounts(codes.concat([value])).then(function (result) {
      var applied =
        result.data.discount_codes &&
        result.data.discount_codes.filter(function (d) {
          return d.code === value;
        })[0];
      var errorEl = document.querySelector('[data-ccd-discount-error]');
      if (applied && applied.applicable === false) {
        if (errorEl) errorEl.classList.remove('hidden');
      } else if (errorEl) {
        errorEl.classList.add('hidden');
      }
    });

    input.value = '';
  });

  document.addEventListener('input', function (event) {
    var input = event.target.closest('.ccd__qty-input');
    if (!input) return;

    clearTimeout(input._ccdTimer);
    input._ccdTimer = setTimeout(function () {
      var key = input.getAttribute('data-key');
      var qty = parseInt(input.value || '0', 10);
      if (Number.isNaN(qty) || qty < 0) qty = 0;
      qty = clampToMax(input, qty);
      changeLine(key, qty);
    }, 500);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') close();
  });

  // Shim the Slide Cart app's public API so unrelated glue code (e.g.
  // gift-with-purchase.js) that still calls it keeps working unmodified.
  window.SLIDECART_OPEN = open;
  window.SLIDECART_CLOSE = close;
  window.SLIDECART_UPDATE = refresh;

  window.CartDrawer = {
    open: open,
    close: close,
    refresh: refresh,
    addToCart: addToCart,
    changeLine: changeLine,
    applyCartResponse: applyCartResponse,
  };

  // The initial page load already has a server-rendered #CustomCartDrawer
  // with correct data-auto-gift-action / data-samples-overflow attributes
  // (no fetch involved), but render() — where these are normally acted on —
  // only runs after a cart mutation. Run them once up front too, so a
  // trigger product already sitting in the cart from an earlier visit still
  // gets its gift added without requiring another cart change first.
  processAutoGift();
  trimSampleOverflow();
})();
